/* ========================================================================
 * Apricot's Filters
 * ======================================================================== */

// SCSS
import '../scss/includes/apricot-base.scss';
import '../scss/includes/filter.scss';

import Popover from './CBPopover';
import Utils from './CBUtils';

/**
 * Filter Popover
 *
 * @export
 * @param {Object} data
 * @param {Element} data.elem
 * @param {Element} data.popoverNode
 * @param {Boolean} data.shadowRoot
 * @param {String} data.placement
 * @param {String} data.flipVariations
 * @param {Array} data.offset
 * @param {Number|Object} data.delay
 * @param {Boolean} data.closeOnClickOutside
 * @param {Boolean} data.disableHeightAdjustment
 * @param {Boolean} data.disableHeightAdjustmentAria
 * @param {Boolean} data.splitBtn
 * @param {Function} data.onShow
 * @param {Function} data.onHide
 * @returns {{hide: Function}}
 * @returns {{show: Function}}
 * @returns {{updateTrigger: Function}}
 * @returns {{clear: Function}}
 * @returns {{destroy: Function}}
 */

const CBFilter = (data = {}) => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return {
      hide: () => {},
      show: () => {},
      updateTrigger: () => {},
      clear: () => {},
      destroy: () => {},
    };
  }
  const defaultData = {
    elem: null,
    popoverNode: null,
    shadowRoot: false,
    placement: 'bottom-start',
    flipVariations: 'top-start',
    offset: [0, 8],
    delay: {
      show: 200,
      hide: 100,
    },
    closeOnClickOutside: true,
    disableHeightAdjustment: false,
    disableHeightAdjustmentAria: true,
    splitBtn: false,
    clear: null,
    onShow: null,
    onHide: null,
  };

  data = {
    ...defaultData,
    ...data,
  };

  let elem = data.splitBtn ? data.elem?.parentElement : data.elem;
  if (!Utils.elemExists(elem)) return null;

  let popoverNode = null;
  let header = null;
  let footer = null;
  let splitBtnLabel = data.splitBtn ? elem.querySelector('.cb-filter-label') : null;
  let splitBtnClear = data.splitBtn ? elem.querySelector('.cb-btn-close') : null;

  let shown = false;
  let adjustableContent = null;
  let triggerLabel = data.splitBtn ? splitBtnLabel?.textContent.trim() : elem.textContent.trim();

  let popoverIns = null;

  const resetHeight = () => {
    adjustableContent.style.overflowY = 'hidden';

    adjustableContent.style.height = 'auto';
    if (!data.disableHeightAdjustmentAria) {
      Utils.removeAttr(adjustableContent, 'tabindex');

      Utils.removeAttr(adjustableContent, 'role');

      Utils.removeAttr(adjustableContent, 'aria-label');
    }
  };

  const calculateHeight = () => {
    if (!shown) return;

    let ch = Utils.height(popoverNode);
    let mh = Utils.height(adjustableContent);
    let hh = 0;
    let fh = 0;
    let newHeight = 0;

    if (header) hh = Utils.outerHeight(header);
    if (footer) {
      fh = Utils.outerHeight(footer);
    }

    newHeight = ch - 40 - hh - fh;
    if (mh > newHeight) {
      adjustableContent.style.overflowY = 'auto';

      adjustableContent.style.height = newHeight + 'px';
      if (!data.disableHeightAdjustmentAria) {
        Utils.attr(adjustableContent, 'tabindex', '0');

        Utils.attr(adjustableContent, 'role', 'region');

        Utils.attr(adjustableContent, 'aria-label', 'scrollable content');
      }
    } else {
      resetHeight();
    }
  };

  const popoverShow = e => {
    data.onShow && data.onShow(e);

    Utils.addClass(elem, 'cb-filter-open');
  };
  const popoverHide = e => {
    data.onHide && data.onHide(e);

    Utils.removeClass(elem, 'cb-filter-open');

    if (splitBtnLabel) {
      splitBtnLabel.focus();
    }
  };
  const hide = () => {
    popoverIns.hide();
  };
  const show = () => {
    popoverIns.show();
  };

  /**
   * Updates the label for trigger
   * @method CBFilter#updateTrigger
   * @memberof CBFilter
   * @param {Number} count - Number of selected filters
   * @param {String} label - Actual label of the trigger (optional)
   */
  const updateTrigger = (count = 0, label = null) => {
    let newLabel = triggerLabel;
    let active = false;
    if (label) newLabel = label;

    if (count > 0) {
      newLabel = `${newLabel} (${count})`;
    } else {
      newLabel = triggerLabel;
    }

    if (splitBtnLabel) {
      splitBtnLabel.textContent = newLabel;
    } else {
      elem.textContent = newLabel;
    }

    if (triggerLabel !== newLabel) {
      Utils.addClass(elem, 'cb-filter-active');

      active = true;
    } else {
      Utils.removeClass(elem, 'cb-filter-active');
    }

    if (splitBtnClear) {
      if (active) {
        Utils.removeClass(splitBtnClear, 'cb-hidden');

        Utils.attr(splitBtnClear, 'aria-hidden', 'false');

        Utils.attr(splitBtnClear, 'tabindex', '0');
      } else {
        Utils.addClass(splitBtnClear, 'cb-hidden');

        Utils.attr(splitBtnClear, 'aria-hidden', 'true');

        Utils.attr(splitBtnClear, 'tabindex', '-1');
      }
    }
  };

  const handleClick = e => {
    e.preventDefault();

    if (Utils.hasClass(elem, 'cb-filter-open')) {
      popoverHide(e);
    } else {
      popoverShow(e);
    }
  };

  const handlePopoverHide = e => {
    popoverHide(e);
  };

  const handleClearClick = e => {
    e.preventDefault();

    e.stopPropagation();

    // splitBtnLabel.focus();

    if (typeof data.clear === 'function') {
      data.clear();
    }
  };

  const handlePopoverShow = () => {
    shown = true;

    calculateHeight();
  };

  const handlePopoverHideReset = () => {
    shown = false;

    resetHeight();
  };

  const destroy = () => {
    if (elem.filterPlugin === 'cb') {
      elem.removeEventListener('click', handleClick);

      elem.removeEventListener('apricot_popover_hide', handlePopoverHide);

      elem.removeEventListener('apricot_popover_show', handlePopoverShow);

      elem.removeEventListener('apricot_popover_hide', handlePopoverHideReset);

      splitBtnClear?.removeEventListener('click', handleClearClick);

      if (popoverIns) {
        popoverIns.destroy();

        popoverIns = null;
      }

      Utils.removeClass(elem, 'cb-filter-open');

      Utils.removeClass(elem, 'cb-filter-active');

      elem.filterPlugin = null;
    }
  };

  const init = () => {
    elem.filterPlugin = 'cb';

    popoverNode = data.popoverNode || document.querySelector(`#${elem.getAttribute('aria-controls')}`);

    adjustableContent = popoverNode.querySelector('.cb-popover-content');

    header = popoverNode.querySelector('.cb-popover-header');

    footer = popoverNode.querySelector('.cb-popover-footer');

    triggerLabel = data.splitBtn ? splitBtnLabel?.textContent.trim() : elem.textContent.trim();

    if (data.splitBtn && splitBtnLabel && splitBtnClear) {
      const labelId = Utils.attr(splitBtnLabel, 'id') || Utils.uniqueID(5, 'apricot_');
      Utils.attr(splitBtnLabel, 'id', labelId);

      Utils.attr(splitBtnClear, 'aria-describedby', labelId);
    }

    popoverIns = Popover({
      elem: data.splitBtn ? splitBtnLabel : elem,
      popoverNode: popoverNode,
      placement: data.placement,
      flipVariations: data.flipVariations,
      offset: data.offset,
      delay: data.delay,
      closeOnClickOutside: data.closeOnClickOutside,
      filter: true,
      shadowRoot: data.shadowRoot,
    });

    elem.addEventListener('click', handleClick);

    elem.addEventListener('apricot_popover_hide', handlePopoverHide);

    splitBtnClear?.addEventListener('click', handleClearClick);

    if (!data.disableHeightAdjustment && Utils.elemExists(adjustableContent)) {
      Utils.addClass(popoverNode, 'cb-popover-content-adjust');

      elem.addEventListener('apricot_popover_show', handlePopoverShow);

      elem.addEventListener('apricot_popover_hide', handlePopoverHideReset);
    }
  };

  if (elem.filterPlugin !== 'cb') {
    init();
  }

  return {
    hide: hide,
    show: show,
    updateTrigger: updateTrigger,
    clear: handleClearClick,
    destroy: destroy,
  };
};

export default CBFilter;
